import { useMemo, useState } from 'react'
import ReactApexChart from 'react-apexcharts'
import { ApexOptions } from 'apexcharts'
import { useAiFilters } from './AiFilterContext'
import {
  buildDailySeries,
  eventImpactBars,
  kpisFromSeries,
  monthlySeasonality,
  operationalSplit,
  profileByHour,
  sitesForCompany,
  toIso,
  weatherScatter,
  weekdayHourHeatmap,
  yearlyTrend,
} from '../profiles/dashboard/catalog'
import { ChartFrame, baseChart, tightGrid } from '../profiles/dashboard/ChartFrame'

type Section =
  | 'overview'
  | 'timeseries'
  | 'season'
  | 'events'
  | 'weather'
  | 'profiles'
  | 'operational'

const sections: { id: Section; label: string }[] = [
  { id: 'overview', label: 'Overview' },
  { id: 'timeseries', label: 'Time series' },
  { id: 'season', label: 'Seasonality' },
  { id: 'events', label: 'Events' },
  { id: 'weather', label: 'Weather' },
  { id: 'profiles', label: 'Profiles' },
  { id: 'operational', label: 'Operational' },
]

const AiDataAnalysisView = () => {
  const { company, site, siteDef, horizon } = useAiFilters()
  const [section, setSection] = useState<Section>('overview')

  const range = useMemo(() => {
    const end = new Date()
    const start = new Date()
    start.setDate(end.getDate() - Math.max(29, horizon.value * 3 - 1))
    return { start: toIso(start), end: toIso(end) }
  }, [horizon.value])

  const companySites = useMemo(() => sitesForCompany(company.value), [company.value])
  const series = useMemo(
    () => buildDailySeries(siteDef, range.start, range.end),
    [siteDef, range.start, range.end]
  )
  const kpis = useMemo(() => kpisFromSeries(series), [series])
  const season = useMemo(() => monthlySeasonality(series), [series])
  const heat = useMemo(() => weekdayHourHeatmap(siteDef), [siteDef])
  const events = useMemo(() => eventImpactBars(series), [series])
  const weather = useMemo(() => weatherScatter(series), [series])
  const ops = useMemo(() => operationalSplit(series), [series])
  const profiles = useMemo(() => profileByHour(siteDef), [siteDef])
  const yearly = useMemo(() => yearlyTrend(siteDef), [siteDef])

  const tsOptions: ApexOptions = useMemo(
    () => ({
      chart: { ...baseChart, type: 'line', toolbar: { show: false } },
      grid: tightGrid,
      stroke: { curve: 'smooth', width: 2.5 },
      dataLabels: { enabled: false },
      xaxis: {
        categories: series.map((r) => r.date.slice(5)),
        title: { text: 'Date' },
        labels: { hideOverlappingLabels: true, trim: true },
      },
      yaxis: {
        title: { text: 'kWh / day' },
        labels: {
          formatter: (v) => (v >= 1000 ? `${(v / 1000).toFixed(1)}k` : `${Math.round(v)}`),
        },
      },
      colors: ['#50CD89', '#3E97FF'],
      legend: { position: 'top', horizontalAlign: 'left', floating: false },
      tooltip: {
        y: { formatter: (v) => `${Math.round(Number(v)).toLocaleString()} kWh` },
      },
    }),
    [series]
  )

  return (
    <>
      <div className='d-flex flex-wrap gap-2 mb-5'>
        {sections.map((s) => (
          <button
            key={s.id}
            type='button'
            className={`btn btn-sm rounded-pill px-4 ${
              section === s.id ? 'btn-primary' : 'btn-light'
            }`}
            onClick={() => setSection(s.id)}
          >
            {s.label}
          </button>
        ))}
      </div>

      {section === 'overview' && (
        <>
          <div className='row g-4 mb-5'>
            {companySites.map((s) => (
              <div className='col-md-4 col-xl-2' key={s.id}>
                <div
                  className={`card h-100 border-0 shadow-sm ${
                    s.id === site.value ? 'border border-primary' : ''
                  }`}
                >
                  <div className='card-body'>
                    <div className='fs-8 text-muted text-uppercase'>
                      {s.type.replace(/_/g, ' ')}
                    </div>
                    <div className='fw-bold fs-6 mb-2'>{s.name}</div>
                    <div className='fs-2 fw-bold text-primary'>{s.baseKwh.toLocaleString()}</div>
                    <div className='fs-8 text-muted'>baseline kWh/day</div>
                  </div>
                </div>
              </div>
            ))}
          </div>
          <div className='row g-4'>
            <div className='col-lg-7' style={{ minWidth: 0 }}>
              <div className='card border-0 shadow-sm h-100 overflow-hidden'>
                <div className='card-header border-0 pt-6'>
                  <h3 className='card-title fw-bold fs-4'>Yearly trend · {site.label}</h3>
                </div>
                <div className='card-body pt-0' style={{ minWidth: 0 }}>
                  <ChartFrame>
                    <ReactApexChart
                      options={{
                        chart: { ...baseChart, type: 'area', toolbar: { show: false } },
                        grid: tightGrid,
                        stroke: { curve: 'smooth', width: 2 },
                        fill: {
                          type: 'gradient',
                          gradient: { opacityFrom: 0.4, opacityTo: 0.05 },
                        },
                        dataLabels: { enabled: false },
                        xaxis: { categories: yearly.map((y) => y.year) },
                        yaxis: {
                          labels: {
                            formatter: (v) =>
                              v >= 1000 ? `${(v / 1000).toFixed(1)}k` : `${Math.round(v)}`,
                          },
                        },
                        colors: ['#3E97FF'],
                        legend: { show: false },
                      }}
                      series={[{ name: 'Mean kWh/day', data: yearly.map((y) => y.mean) }]}
                      type='area'
                      width='100%'
                      height={300}
                    />
                  </ChartFrame>
                </div>
              </div>
            </div>
            <div className='col-lg-5' style={{ minWidth: 0 }}>
              <div className='card border-0 shadow-sm h-100 overflow-hidden'>
                <div className='card-header border-0 pt-6'>
                  <h3 className='card-title fw-bold fs-4'>Weekday × hour</h3>
                </div>
                <div className='card-body pt-0' style={{ minWidth: 0 }}>
                  <ChartFrame>
                    <ReactApexChart
                      key={`heat-${site.value}`}
                      options={{
                        chart: { ...baseChart, type: 'heatmap', toolbar: { show: false } },
                        grid: tightGrid,
                        dataLabels: { enabled: false },
                        colors: ['#3E97FF'],
                        plotOptions: {
                          heatmap: {
                            shadeIntensity: 0.55,
                            radius: 2,
                            colorScale: {
                              min: 0,
                              max: 140,
                              ranges: [
                                { from: 0, to: 50, color: '#E1F0FF', name: 'Low' },
                                { from: 51, to: 90, color: '#3E97FF', name: 'Mid' },
                                { from: 91, to: 160, color: '#F1416C', name: 'Peak' },
                              ],
                            },
                          },
                        },
                        legend: { show: true, position: 'top', floating: false },
                        xaxis: { type: 'category', labels: { trim: true } },
                      }}
                      series={heat}
                      type='heatmap'
                      width='100%'
                      height={300}
                    />
                  </ChartFrame>
                </div>
              </div>
            </div>
          </div>
        </>
      )}

      {section === 'timeseries' && (
        <div className='card border-0 shadow-sm overflow-hidden'>
          <div className='card-header border-0 pt-6'>
            <h3 className='card-title fw-bold fs-4'>Actual vs forecast · {site.label}</h3>
            <div className='card-toolbar'>
              <span className='badge badge-light-primary'>
                Avg {kpis.avg.toLocaleString()} kWh · WAPE {kpis.wape}%
              </span>
            </div>
          </div>
          <div className='card-body pt-0' style={{ minWidth: 0 }}>
            <ChartFrame>
              <ReactApexChart
                options={tsOptions}
                series={[
                  { name: 'Actual', data: series.map((r) => r.actual) },
                  { name: 'Forecast', data: series.map((r) => r.predicted) },
                ]}
                type='line'
                width='100%'
                height={360}
              />
            </ChartFrame>
          </div>
        </div>
      )}

      {section === 'season' && (
        <div className='card border-0 shadow-sm overflow-hidden'>
          <div className='card-header border-0 pt-6'>
            <h3 className='card-title fw-bold fs-4'>Monthly seasonality · {site.label}</h3>
          </div>
          <div className='card-body pt-0' style={{ minWidth: 0 }}>
            <ChartFrame>
              <ReactApexChart
                options={{
                  chart: { ...baseChart, type: 'line', toolbar: { show: false } },
                  grid: tightGrid,
                  stroke: { curve: 'smooth', width: 3 },
                  dataLabels: { enabled: false },
                  xaxis: { categories: season.categories },
                  yaxis: { title: { text: 'Index vs mean' } },
                  colors: ['#009EF7'],
                  legend: { show: false },
                  annotations: { yaxis: [{ y: 1, borderColor: '#A1A5B7', strokeDashArray: 4 }] },
                }}
                series={[{ name: 'Seasonality index', data: season.values }]}
                type='line'
                width='100%'
                height={340}
              />
            </ChartFrame>
          </div>
        </div>
      )}

      {section === 'events' && (
        <div className='card border-0 shadow-sm overflow-hidden'>
          <div className='card-header border-0 pt-6'>
            <h3 className='card-title fw-bold fs-4'>Event impact · {site.label}</h3>
            <div className='card-toolbar'>
              <span className='badge badge-light'>
                Uplift {kpis.upliftPct > 0 ? '+' : ''}
                {kpis.upliftPct}%
              </span>
            </div>
          </div>
          <div className='card-body pt-0' style={{ minWidth: 0 }}>
            {events.length ? (
              <ChartFrame>
                <ReactApexChart
                  options={{
                    chart: { ...baseChart, type: 'bar', toolbar: { show: false } },
                    grid: tightGrid,
                    plotOptions: { bar: { columnWidth: '45%', borderRadius: 4 } },
                    dataLabels: { enabled: true, formatter: (v) => `${v}×` },
                    xaxis: {
                      categories: events.map((e) => e.label),
                      labels: { rotate: -35, hideOverlappingLabels: true, trim: true },
                    },
                    yaxis: { title: { text: 'Ratio vs baseline' } },
                    colors: ['#FFC700'],
                    legend: { show: false },
                  }}
                  series={[{ name: 'Ratio vs baseline', data: events.map((e) => e.ratio) }]}
                  type='bar'
                  width='100%'
                  height={340}
                />
              </ChartFrame>
            ) : (
              <div className='text-muted text-center py-10'>No events in selected horizon</div>
            )}
          </div>
        </div>
      )}

      {section === 'weather' && (
        <div className='card border-0 shadow-sm overflow-hidden'>
          <div className='card-header border-0 pt-6'>
            <h3 className='card-title fw-bold fs-4'>Temperature vs load · {site.label}</h3>
          </div>
          <div className='card-body pt-0' style={{ minWidth: 0 }}>
            <ChartFrame>
              <ReactApexChart
                options={{
                  chart: {
                    ...baseChart,
                    type: 'scatter',
                    toolbar: { show: false },
                    zoom: { enabled: true },
                  },
                  grid: tightGrid,
                  xaxis: { title: { text: 'Temperature (°C)' } },
                  yaxis: {
                    title: { text: 'kWh (non-event)' },
                    labels: {
                      formatter: (v) =>
                        v >= 1000 ? `${(v / 1000).toFixed(1)}k` : `${Math.round(v)}`,
                    },
                  },
                  colors: ['#50CD89'],
                  markers: { size: 5 },
                  legend: { show: false },
                }}
                series={[{ name: 'Non-event days', data: weather }]}
                type='scatter'
                width='100%'
                height={340}
              />
            </ChartFrame>
          </div>
        </div>
      )}

      {section === 'profiles' && (
        <div className='card border-0 shadow-sm overflow-hidden'>
          <div className='card-header border-0 pt-6'>
            <h3 className='card-title fw-bold fs-4'>Daily profiles · {site.label}</h3>
          </div>
          <div className='card-body pt-0' style={{ minWidth: 0 }}>
            <ChartFrame>
              <ReactApexChart
                options={{
                  chart: { ...baseChart, type: 'line', toolbar: { show: false } },
                  grid: tightGrid,
                  stroke: { curve: 'smooth', width: 2 },
                  dataLabels: { enabled: false },
                  xaxis: { categories: profiles.categories, title: { text: 'Hour' } },
                  yaxis: { title: { text: 'kWh' } },
                  colors: ['#3E97FF', '#50CD89', '#F1416C'],
                  legend: { position: 'top', floating: false },
                }}
                series={profiles.series}
                type='line'
                width='100%'
                height={360}
              />
            </ChartFrame>
          </div>
        </div>
      )}

      {section === 'operational' && (
        <div className='card border-0 shadow-sm overflow-hidden'>
          <div className='card-header border-0 pt-6'>
            <h3 className='card-title fw-bold fs-4'>Operational split · {site.label}</h3>
          </div>
          <div className='card-body pt-0' style={{ minWidth: 0 }}>
            <ChartFrame>
              <ReactApexChart
                options={{
                  chart: { ...baseChart, type: 'bar', toolbar: { show: false } },
                  grid: { ...tightGrid, padding: { left: 8, right: 16, top: 8, bottom: 0 } },
                  plotOptions: { bar: { horizontal: true, borderRadius: 4, barHeight: '70%' } },
                  dataLabels: { enabled: true },
                  colors: ['#7239EA'],
                  legend: { show: false },
                  xaxis: {
                    categories: ops.map((o) => `${o.label} (n=${o.count})`),
                    labels: { formatter: (v) => `${(Number(v) / 1000).toFixed(1)}k` },
                  },
                  yaxis: { labels: { maxWidth: 140 } },
                }}
                series={[{ name: 'Mean daily kWh', data: ops.map((o) => o.mean) }]}
                type='bar'
                width='100%'
                height={300}
              />
            </ChartFrame>
          </div>
        </div>
      )}
    </>
  )
}

export default AiDataAnalysisView
